import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import DialogCustom from 'commons/DialogCustom';
import Text from 'commons/Text';
import API from 'configs/API';
import Colors from 'configs/Colors';
import useDebounce from 'hooks/useDebounce';
import { ToastTopHelper } from 'utils/utils';

import useCreateShareLink from '../hooks/useCreateShareLink';
import useRemoveShareLinkRecipient from '../hooks/useRemoveShareLinkRecipient';
import useShareLinkRecipients from '../hooks/useShareLinkRecipients';
import AccessListSection from './AccessListSection';
import SearchInviteSection from './SearchInviteSection';

const ShareTabInviteUser = ({ classes, onClose }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.account.user);

  const [inputValue, setInputValue] = useState('');
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [isRemoveAccessDialogOpen, setIsRemoveAccessDialogOpen] = useState(false);
  const [personToRemove, setPersonToRemove] = useState(null);
  const queryClient = useQueryClient();

  const inputValueDebounce = useDebounce(inputValue, 500);

  const { data: userOptions = [] } = useQuery({
    queryKey: [API.AUTH.SEARCH_USER, { search: inputValueDebounce, exclude__in: user?.id }],
    enabled: !!inputValueDebounce,
  });

  const { data: sharedUsersData, fetchNextPage } = useShareLinkRecipients(user?.id);

  const handleSelectedChange = (_, value) => {
    setSelectedProfiles(value);
  };

  const handleScrollAccessList = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    if (scrollTop + clientHeight >= scrollHeight - 1) {
      fetchNextPage();
    }
  };

  const { mutate: createShareLink, isPending } = useCreateShareLink();
  const { mutate: removeRecipient } = useRemoveShareLinkRecipient();

  const handleOpenRemoveAccessDialog = (person) => {
    setPersonToRemove(person);
    setIsRemoveAccessDialogOpen(true);
  };

  const handleCloseRemoveAccessDialog = () => {
    setIsRemoveAccessDialogOpen(false);
    setPersonToRemove(null);
  };

  const handleConfirmRemoveAccess = () => {
    if (!personToRemove?.id || !user?.id) {
      handleCloseRemoveAccessDialog();
      return;
    }

    removeRecipient(
      {
        userId: user.id,
        recipientId: personToRemove.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [API.ARTWORK.SHARE_LINK_RECIPIENTS(user?.id)],
          });
          handleCloseRemoveAccessDialog();
          ToastTopHelper.success(t('remove_access_success'));
        },
      },
    );
  };

  const handleSubmitInvite = async () => {
    if (!selectedProfiles.length) {
      ToastTopHelper.error(t('no_user_selected'));
      return;
    }
    const recipientIds = selectedProfiles.map((item) => item.id);

    const payload = {
      is_share_all: true,
      recipient_type: 'specific',
      expiration_type: 'never',
      share_type: 'mixed',
      recipient_user_ids: recipientIds,
    };

    createShareLink(
      { payload },
      {
        onSuccess: () => {
          setSelectedProfiles([]);
          ToastTopHelper.success(t('share_success'));
        },
        onError: (error) => {
          ToastTopHelper.error(t(error.message || 'create_share_link_failed'));
        },
      },
    );
    onClose();
  };

  return (
    <>
      <SearchInviteSection
        selectedProfiles={selectedProfiles}
        options={userOptions}
        inputValue={inputValue}
        onSelectedChange={handleSelectedChange}
        onInputChange={setInputValue}
        onSubmit={handleSubmitInvite}
        isSubmitting={isPending}
        classes={classes}
        t={t}
      />
      <AccessListSection
        accessListData={sharedUsersData}
        onScroll={handleScrollAccessList}
        onRemove={handleOpenRemoveAccessDialog}
        classes={classes}
        t={t}
      />

      <DialogCustom
        isShowDialog={isRemoveAccessDialogOpen}
        onCancel={handleCloseRemoveAccessDialog}
        onSubmit={handleConfirmRemoveAccess}
        dialogTitle={t('remove_access')}
        textSubmit={t('remove')}
        textCancel={t('cancel')}
        maxWidth="xs"
        submitButtonStyle={classes.buttonSubmit}
      >
        <div>
          <Text
            type="sm"
            color={Colors.Grey10}
            style={{ marginBottom: 12 }}
          >
            {t('remove_access_confirmation', {
              name: personToRemove?.legal_name || personToRemove?.name || '',
            })}
          </Text>
          <Text
            type="xs"
            color={Colors.Grey7}
          >
            {t('remove_access_note')}
          </Text>
        </div>
      </DialogCustom>
    </>
  );
};

export default ShareTabInviteUser;
